import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { EventTypes } from '../../types';
import { EventMapPopupContent } from '../event-map-popup-content';
import Carousel from '../custom-carousel/CustomCarousel';

interface Props {
    map: google.maps.Map | null;
    event: EventTypes.Model | null;
    multipleEvents?: EventTypes.Model[] | null;
    visible: boolean;
    onClose: () => void;
    onSeeMore: () => void;
    setSelectedEvent?: (event: EventTypes.Model) => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

// Buffer zone in pixels around the visible card for hover detection
const HOVER_BUFFER = 20;

// Animation duration in ms
const ANIMATION_DURATION = 150;

export const CustomMapPopup: React.FC<Props> = ({
    map,
    event,
    multipleEvents,
    visible,
    onClose,
    onSeeMore,
    setSelectedEvent,
    onMouseEnter,
    onMouseLeave,
}) => {
    const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);

    // Calculate pixel position from lat/lng
    const updatePosition = useCallback(() => {
        if (!map || !event) return;

        const projection = map.getProjection();
        if (!projection) return;

        const bounds = map.getBounds();
        if (!bounds) return;

        const latLng = new google.maps.LatLng(event.latitude, event.longitude);
        const topRight = projection.fromLatLngToPoint(bounds.getNorthEast()!);
        const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest()!);
        const scale = Math.pow(2, map.getZoom()!);
        const worldPoint = projection.fromLatLngToPoint(latLng);

        if (!topRight || !bottomLeft || !worldPoint) return;

        const x = (worldPoint.x - bottomLeft.x) * scale;
        const y = (worldPoint.y - topRight.y) * scale;

        setPosition({ x, y });
    }, [map, event]);

    // Update position when map moves or zooms
    useEffect(() => {
        if (!map || !visible) return;

        const listeners = [
            map.addListener('idle', updatePosition),
            map.addListener('zoom_changed', updatePosition),
            map.addListener('drag', updatePosition),
            map.addListener('bounds_changed', updatePosition),
        ];

        // Initial position calculation
        updatePosition();

        return () => {
            listeners.forEach(listener => google.maps.event.removeListener(listener));
        };
    }, [map, visible, updatePosition]);

    // Handle visibility and animation
    useEffect(() => {
        if (visible) {
            setShouldRender(true);
            setIsAnimatingOut(false);
        } else if (shouldRender) {
            // Start close animation
            setIsAnimatingOut(true);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsAnimatingOut(false);
            }, ANIMATION_DURATION);
            return () => clearTimeout(timer);
        }
    }, [visible, shouldRender]);

    // Forward wheel events from the popup down to the map so scroll/pinch zoom
    // still works while hovering the popup. Without this, the popup intercepts
    // wheel events and on Mac trackpad pinch the browser falls back to zooming
    // the whole page (Ctrl+wheel default). Must use a non-passive native
    // listener — React's synthetic onWheel is passive and can't preventDefault.
    useEffect(() => {
        const el = popupRef.current;
        if (!el || !map || !shouldRender) return;
        const handler = (e: WheelEvent) => {
            e.preventDefault();
            map.getDiv().dispatchEvent(
                new WheelEvent('wheel', {
                    deltaY: e.deltaY,
                    deltaX: e.deltaX,
                    deltaMode: e.deltaMode,
                    clientX: e.clientX,
                    clientY: e.clientY,
                    ctrlKey: e.ctrlKey,
                    bubbles: true,
                })
            );
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }, [map, shouldRender]);

    // Don't render if not visible and not animating
    if (!shouldRender || !position || !event) return null;

    const events = multipleEvents?.length ? multipleEvents : [event];
    const showSlider = multipleEvents && multipleEvents.length > 1;

    const slides: React.ReactNode[] = [];
    if (showSlider) {
        multipleEvents?.forEach((evt, index) => {
            slides.push(
                <Box key={index}>
                    <EventMapPopupContent
                        isCarousel={true}
                        event={evt}
                        onSeeMore={() => {
                            setSelectedEvent?.(evt);
                            onSeeMore();
                        }}
                    />
                </Box>
            );
        });
    }

    return (
        <div
            ref={popupRef}
            style={{
                position: 'absolute',
                left: 0,
                top: 0,
                transform: `translate(${position.x}px, ${position.y}px) translate(-50%, -100%)`,
                zIndex: 1000,
                pointerEvents: 'auto',
                willChange: 'transform, opacity',
            }}
        >
            {/* Hover detection zone - includes invisible buffer around the card */}
            <div
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                style={{
                    padding: `${HOVER_BUFFER}px`,
                    marginBottom: '-10px', // Account for the pointer tip
                }}
            >
                {/* Visible card container */}
                <div
                    style={{
                        position: 'relative',
                        background: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
                        minWidth: '250px',
                        maxWidth: '300px',
                        opacity: isAnimatingOut ? 0 : 1,
                        transform: isAnimatingOut ? 'scale(0.95)' : 'scale(1)',
                        transition: `opacity ${ANIMATION_DURATION}ms ease-out, transform ${ANIMATION_DURATION}ms ease-out`,
                    }}
                >
                    {/* Content */}
                    <div style={{ padding: '16px' }}>
                        {!showSlider ? (
                            <EventMapPopupContent event={event} onSeeMore={onSeeMore} />
                        ) : (
                            <Carousel showDots={true} showArrows={true}>
                                {slides}
                            </Carousel>
                        )}
                    </div>

                    {/* Pointer tip (triangle pointing down to marker) */}
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '-10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '12px solid transparent',
                            borderRight: '12px solid transparent',
                            borderTop: '12px solid white',
                            filter: 'drop-shadow(0 2px 2px rgba(0, 0, 0, 0.1))',
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default CustomMapPopup;
